import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import {
  BRAND_PLANS_PATH,
  BRAND_SERVICES_PATH,
  ROUTES,
  enquiryDetailPath,
} from "../../../routes/paths";
import PageHeader from "../../../components/layout/PageHeader";
import Pagination from "../../../components/ui/Pagination";
import {
  AddAction,
  Button,
  EmptyStateProps,
  MobileSearchField,
  failedEmptyState,
  searchEmptyState,
  useIsMobile,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
// The one screen-sized illustration on this page, and only for the workspace
// that has never had an enquiry — see `EmptyState`'s `welcome` variant.
import { ReactComponent as FirstEnquiryMark } from "../../../assets/svg/No data images/enquiry.svg";
import ChangeStatusModal from "../components/ChangeStatusModal";
import EnquiriesTable from "../components/EnquiriesTable";
import EnquiryCards from "../components/EnquiryCards";
// The two questions a row's tick and cross raise, asked here in the same words
// a client's payment history asks them in — see `ConfirmPaymentModal`.
import ConfirmPaymentModal from "../../payments/components/ConfirmPaymentModal";
import DeclinePaymentModal from "../../payments/components/DeclinePaymentModal";
import { useEnquiries } from "../hooks/useEnquiries";
import { useEnquiryPaymentRequests } from "../hooks/useEnquiryPaymentRequests";
// A deep import rather than the billing barrel: that barrel pulls in the
// checkout and result screens, and this page only needs to know whether the
// workspace is allowed to add anything.
import { useSubscriptionGate } from "../../billing/hooks/useSubscriptionGate";
// Deep imports for the same reason: the brand barrel pulls in the whole My
// brand screen, and this page only needs to know whether the workspace has
// said what it sells and what it does, and how to say so if it has not.
import PlansRequiredModal from "../../brand/components/PlansRequiredModal";
import ServicesRequiredModal from "../../brand/components/ServicesRequiredModal";
import { usePlansCreated } from "../../brand/hooks/usePlansCreated";
import { useServicesCreated } from "../../brand/hooks/useServicesCreated";
import { useSetupGate } from "../../brand/hooks/useSetupGate";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import {
  cancelEnquiryPayment,
  clearEnquiryStatusOverride,
  confirmEnquiryPayment,
  fetchEnquiry,
  noteEnquiryPayment,
  updateEnquiryStatus,
} from "../services/enquiriesService";
import { ApiError } from "../../../lib/http";
import type {
  Enquiry,
  EnquiryPayment,
  EnquiryStatus,
} from "../types/enquiries.types";

/** What one page of the table holds, and what the endpoint is asked for. */
const ENQUIRIES_PER_PAGE = 10;

/**
 * The one thing a row is being asked about: the request, the enquiry it was
 * raised against, and which of the two answers is being given.
 *
 * The payment travels with it rather than being looked up when the dialog
 * confirms: by then it has been read once already, and a second read is a
 * second chance for the two to disagree.
 */
type PendingAnswer = {
  enquiry: Enquiry;
  payment: EnquiryPayment;
  kind: "confirm" | "decline";
};

const AllEnquiriesPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const getToken = useTokenGetter();

  // Enquiries are one of the three things a plan is what pays for, so with no
  // plan running both ways to New enquiry are off. The list itself stays
  // readable. See `useSubscriptionGate`.
  const gate = useSubscriptionGate();

  // And an enquiry is a lead on what the workspace offers: what a payment is
  // requested for down the pipeline is a plan, and what the lead is raised
  // against is one of the practice's services — the form's Goal field picks
  // from those. A workspace short of either has nothing to take a lead on, so
  // both ways to New enquiry go through the prompt instead — see
  // `useSetupGate`.
  //
  // `status === "ready"` is load-bearing on both: a read still out, or one that
  // failed, leaves the door open rather than standing this in front of somebody
  // who has been selling for a year.
  const { plansCreated, status: plansStatus } = usePlansCreated();
  const { servicesCreated, status: servicesStatus } = useServicesCreated();
  const noPlans = plansStatus === "ready" && !plansCreated;
  const noServices = servicesStatus === "ready" && !servicesCreated;
  const setup = useSetupGate(noPlans || noServices);

  const addEnquiry = setup.guard(() => navigate(ROUTES.addEnquiry));

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  /**
   * Which of the two shapes this screen takes.
   *
   * Not a `md:` class: a table of six columns and a column of cards are not the
   * same markup at two widths, and building both would put every enquiry in the
   * accessibility tree twice — along with two actions menus each, and two live
   * ticks and crosses on every outstanding one. See `EnquiryCards`.
   */
  const isMobile = useIsMobile();
  // The row whose stage is being moved, and the whole of what the dialog is
  // open for. The row rather than its id, so the dialog can name it.
  const [changing, setChanging] = useState<Enquiry | null>(null);
  // Set while the move is with the server, so the dialog's Save spins rather
  // than taking a second press and patching the same enquiry twice.
  const [savingStatus, setSavingStatus] = useState(false);
  // The row whose payment is being answered, and what is being asked of it.
  // Neither answer is something to do by mis-clicking an icon the width of a
  // thumbnail, and the two sit next to each other.
  const [answering, setAnswering] = useState<PendingAnswer | null>(null);
  // The row whose request is being read off the server, so its tick spins for
  // the moment between the press and the question.
  const [reading, setReading] = useState<string | null>(null);
  // The same as `savingStatus`, for the answer being given.
  const [settling, setSettling] = useState(false);

  // The search box and the page are the server's, so changing either refetches
  // rather than narrowing the rows in hand — this table only ever holds one
  // page of them.
  const {
    enquiries: visible,
    count,
    status: loadStatus,
    error,
    reload,
    hasMore,
    loadingMore,
    loadMore,
  } = useEnquiries({
    search,
    page,
    pageSize: ENQUIRIES_PER_PAGE,
    // A phone has no pages under a list — walking one there is scrolling — so
    // it keeps every page fetched and asks for the next as the end of the rows
    // comes into view. The table still turns one page at a time.
    accumulate: isMobile,
  });

  // Settling a request is plainly one of the things a subscription pays for —
  // an enquiry marked paid here is what the practice's own books are read off
  // — so it is shut with the rest. Worked out once: every outstanding row in
  // the table wears the same one.
  const answerGate = gate.props("answer a payment");

  // And moving one along the pipeline is the other thing a row offers. Its own
  // answer rather than the one above, in the same words the enquiry's own
  // screen uses for Change status — a stage is moved the one way wherever it is
  // moved from, so it should read the same in both places.
  const statusGate = gate.props("move an enquiry on");

  // A list that lands quickly goes straight from blank to filled: the table's
  // placeholders are only reached once the fetch has visibly stalled.
  const phase = useLoadPhase(loadStatus === "loading");

  // The invoices behind the rows that are still waiting on one, read as the
  // page lands rather than when a tick is pressed — so the question opens on
  // the press instead of after it. Not worth reading for a workspace that
  // cannot answer them. See `useEnquiryPaymentRequests`.
  useEnquiryPaymentRequests(visible, !gate.locked);

  // The server's count for the search in force, not the rows on screen.
  const totalPages = Math.max(1, Math.ceil(count / ENQUIRIES_PER_PAGE));
  const currentPage = Math.min(page, totalPages);

  // A search that shrank the list past the page in hand lands on the last one,
  // rather than leaving the screen asking for a page that is no longer there.
  // Only where there are pages to be on: a phone holds no page number, and
  // clamping one it is not reading would fight the feed as it grows.
  useEffect(() => {
    if (!isMobile && loadStatus === "ready" && page > totalPages)
      setPage(totalPages);
  }, [isMobile, loadStatus, page, totalPages]);

  // A failure comes before emptiness: an empty table under "no enquiries"
  // would read as an answer, when nothing was ever heard back. Where the
  // server said why, its words replace the generic reassurance — they are
  // more specific than anything written here could be.
  //
  // No retry beneath it: what stops this list is usually something about the
  // workspace rather than a call that dropped, and pressing a button to ask
  // the same question again only gets the same answer back.
  const emptyState = (): EmptyStateProps => {
    if (loadStatus === "failed") {
      const state = failedEmptyState("We could not load your enquiries");
      return error ? { ...state, description: error } : state;
    }

    if (search.trim()) return searchEmptyState("enquiries");

    // An enquiry list with nothing in it is a workspace on its first day
    // rather than a list reporting a result, so this one is the welcome
    // layout: what the screen is for, then the one thing to do about it.
    return {
      variant: "welcome",
      // Sized here rather than left at the file's own 56px — see the same
      // note on the clients page; the class is what keeps the two matched.
      illustration: <FirstEnquiryMark aria-hidden className="w-11 h-11" />,
      title: "Your leads start here",
      description:
        "Record every enquiry so you never miss a potential client.",
      highlights: {
        title: "With enquiries, you can",
        items: [
          "Keep every enquiry organised.",
          <>
            Send an instant <strong className="font-semibold">thank-you email</strong>{" "}
            automatically.
          </>,
          "Convert enquiries into clients when they're ready.",
        ],
      },
      action: (
        <Button
          size="md"
          startIcon={<Plus className="w-4 h-4" />}
          onClick={addEnquiry}
          {...gate.props("add an enquiry")}
        >
          Add enquiry
        </Button>
      ),
    };
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  /**
   * The way to add an enquiry, built once and set in one of two places.
   *
   * Beside the page title on a phone, where the heading row is the only one
   * with width to spare — the search below wants its whole line, and a button
   * sharing it would leave neither of them a thumb's worth. From `md` up it
   * keeps the place it has always had, in the card's header beside the search.
   *
   * Sized to its own words rather than stretched across the row: the menu
   * button shares that line now, and a button told to be the full width of it
   * took the whole line and left the menu underneath. See `PageHeader`.
   */
  const addButton = (
    <AddAction
      label="New enquiry"
      phoneLabel="New"
      className="flex-shrink-0"
      onClick={addEnquiry}
      {...gate.props("add an enquiry")}
    />
  );

  const handleSelectEnquiry = (enquiry: Enquiry) =>
    navigate(enquiryDetailPath(enquiry.id));

  /**
   * Patches the stage on the backend, and only then closes and says so — a
   * dialog that shut on the press would claim a move that may not have landed.
   * The row underneath updates off the store the call writes to, so the table
   * shows the new stage without refetching the page.
   *
   * A refused move leaves the dialog open, holding what was picked, with the
   * server's own words in the toast.
   */
  const handleSaveStatus = async (status: EnquiryStatus) => {
    if (!changing || savingStatus) return;
    setSavingStatus(true);

    try {
      await updateEnquiryStatus(getToken, changing.id, status);
      setChanging(null);
      toast.success("Status updated", `${changing.name} is now ${status}.`);
    } catch (cause) {
      toast.error(
        "Could not update this status",
        cause instanceof ApiError
          ? cause.message
          : "We could not reach the server. Check your connection and try again."
      );
      console.error("enquiry status update failed:", cause);
    } finally {
      setSavingStatus(false);
    }
  };

  /** Both answers fail the same way, and the dialog stays up to be tried again. */
  const reportFailure = (title: string, cause: unknown) => {
    toast.error(
      title,
      cause instanceof ApiError
        ? cause.message
        : "We could not reach the server. Check your connection and try again."
    );
    console.error(`${title.toLowerCase()}:`, cause);
  };

  /**
   * Opens one of the two questions on a row, with the request it is about.
   *
   * The request is in hand for all but the first moment of the page — it is
   * read as the rows land, not when one is pressed. What is left here is the
   * fallback for the press that beat that read, or came after one that failed:
   * the enquiry is fetched then and there, which is the difference between a
   * tick that hesitates once and a tick that does nothing.
   *
   * A request that comes back already settled is not an error, it is news: it
   * was answered on the detail screen, or in another tab, or by the person at
   * the next desk. The row is put right from what came back and the table
   * re-read, rather than a dialog opening over a question nobody is asking.
   */
  const askAboutPayment = async (
    enquiry: Enquiry,
    kind: PendingAnswer["kind"]
  ) => {
    if (reading || settling) return;

    const held = enquiry.payment;
    if (held?.status === "Pending") {
      setAnswering({ enquiry, payment: held, kind });
      return;
    }

    setReading(enquiry.id);

    try {
      const record = await fetchEnquiry(getToken, enquiry.id);
      const request = record.payment;

      if (!request) {
        // Nothing open, and nothing to put in the row's place either — the
        // stage is what is wrong here, so the table is asked again.
        toast.warning(
          "There is no payment to answer",
          `Nothing is outstanding against ${enquiry.name} any more.`
        );
        reload();
        return;
      }

      // Kept either way: a request still open need not be read a second time
      // if the other icon is pressed, and one already settled is what turns
      // the row's icons back into a pill.
      noteEnquiryPayment(enquiry.id, request);

      if (request.status !== "Pending") {
        toast.warning(
          "This payment has already been answered",
          request.status === "Paid"
            ? `${enquiry.name}'s payment was marked as received elsewhere.`
            : `The payment requested from ${enquiry.name} was called off elsewhere.`
        );
        reload();
        return;
      }

      setAnswering({ enquiry, payment: request, kind });
    } catch (cause) {
      reportFailure("Could not open this payment", cause);
    } finally {
      setReading(null);
    }
  };

  /**
   * Reads the list again for the stage of a row that has just been answered.
   *
   * Settling a request is the backend's business as much as this screen's: the
   * confirm says nothing about a stage, but an enquiry that has paid is not at
   * the same point in the pipeline as one that has been asked, and only the
   * server knows where it put it. The "Payment sent" this browser may be
   * holding is dropped first, or it would sit on top of the answer and hide the
   * move. The rows on screen stay put while the next set is fetched, so nothing
   * blinks over a word.
   */
  const refreshStage = (enquiryId: string) => {
    clearEnquiryStatusOverride(enquiryId);
    reload();
  };

  /**
   * Marks the money in, on the day the dialog was told it arrived — the same
   * call the enquiry's own screen and a client's payment history make, through
   * the request's own id. The dialog stays up until the server has it, so a
   * confirm that could not be made leaves the question on screen to answer
   * again rather than closing over a failure.
   */
  const handleConfirmPayment = async (receivedOn: string) => {
    if (!answering || settling) return;
    const { enquiry, payment } = answering;
    setSettling(true);

    try {
      await confirmEnquiryPayment(getToken, enquiry.id, payment, receivedOn);
      setAnswering(null);
      refreshStage(enquiry.id);
      toast.success(
        "Payment confirmed",
        `${enquiry.name}'s payment has been marked as received.`
      );
    } catch (cause) {
      reportFailure("Could not confirm this payment", cause);
    } finally {
      setSettling(false);
    }
  };

  /**
   * Calls the request off, through the same id the confirm settles.
   *
   * The stage is left where it is, which is why there is no re-read after it: a
   * declined payment is not a decision about the person — they may pay by
   * another means, or be asked again — and the row says so itself, since the
   * cancelled request is kept beside it.
   */
  const handleDeclinePayment = async () => {
    if (!answering || settling) return;
    const { enquiry, payment } = answering;
    setSettling(true);

    try {
      await cancelEnquiryPayment(getToken, enquiry.id, payment);
      setAnswering(null);
      toast.success(
        "Payment declined",
        `The payment requested from ${enquiry.name} has been called off.`
      );
    } catch (cause) {
      reportFailure("Could not decline this payment", cause);
    } finally {
      setSettling(false);
    }
  };

  return (
    <div className="min-h-full md:h-full flex flex-col md:overflow-hidden p-page pt-page-top max-md:pb-0">
      {/* On a phone the way to add one stands beside the page's own title:
          this screen's heading row is the only one with width to spare, and the
          search below wants its whole line. From `md` up it keeps the place it
          has always had, inside the card with the search. */}
      <PageHeader
        title="Enquiries"
        actions={isMobile ? addButton : undefined}
      />

      {/* One card holding the lot from `md` up. On a phone there is no card:
          the rows are cards of their own, and a border around a column of
          bordered cards is a box inside a box that costs a phone's width twice
          over — see `EnquiryCards`. */}
      <section
        className={`mt-4 md:mt-5 flex-1 min-h-0 flex flex-col overflow-hidden ${
          isMobile ? "" : "rounded-md border border-line bg-white"
        }`}
      >
        {/* A line of its own at the full width of the screen on a phone — the
            same field the roster and the plans list search with, see
            `MobileSearchField`. The card's title goes with the card: the page
            is titled "Enquiries" three lines above, and "Total enquiries (12)"
            under it is the card heading for a card that is not there. */}
        {isMobile ? (
          <div className="flex-shrink-0 pb-3">
            <MobileSearchField
              value={search}
              onChange={handleSearchChange}
              placeholder="Search enquiries"
            />
          </div>
        ) : (
          <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-6">
            {/* No count until the list is in: a "(0)" beside a heading that is
                still loading reads as an answer rather than as a blank. */}
            <h2 className="text-[18px] 3xl:text-[20px] font-medium text-[#111C2D]">
              Total enquiries{loadStatus === "ready" && ` (${count})`}
            </h2>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="relative w-full sm:w-[260px]">
                <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
                <input
                  type="search"
                  value={search}
                  onChange={(event) => handleSearchChange(event.target.value)}
                  placeholder="Search"
                  aria-label="Search enquiries"
                  className="w-full rounded-md border border-line py-2 pl-9 pr-3 text-[14px] text-ink placeholder:text-ink-subtle focus:outline-none focus:border-brand"
                />
              </div>
              {addButton}
            </div>
          </div>
        )}

        {/* Said as soon as the request goes out, whether or not anything is
            drawn for it: this line is not seen, so it costs nothing to be
            early, and the skeleton rows it stands for are hidden from screen
            readers — they are the shape of an answer rather than the answer. */}
        {loadStatus === "loading" && (
          <p role="status" className="sr-only">
            Loading enquiries…
          </p>
        )}

        {/* Rendered rather than hidden: a table and a column of cards holding
            the same enquiries would be the same rows twice in the
            accessibility tree, and everything pressable inside the hidden half
            would still be reachable by keyboard.

            No pager under the cards — the list grows as it is scrolled, and
            the pages come off the server as they are reached rather than out
            of rows already in hand. See `accumulate` on `useEnquiries`. */}
        {isMobile ? (
          <EnquiryCards
            enquiries={visible}
            phase={phase}
            empty={emptyState()}
            onSelectEnquiry={handleSelectEnquiry}
            onChangeStatus={setChanging}
            statusGate={statusGate}
            answerGate={answerGate}
            answering={reading}
            onConfirmPayment={(enquiry) => askAboutPayment(enquiry, "confirm")}
            onDeclinePayment={(enquiry) => askAboutPayment(enquiry, "decline")}
            hasMore={hasMore}
            loadingMore={loadingMore}
            onEndReached={loadMore}
          />
        ) : (
          <EnquiriesTable
            enquiries={visible}
            phase={phase}
            empty={emptyState()}
            onSelectEnquiry={handleSelectEnquiry}
            onChangeStatus={setChanging}
            statusGate={statusGate}
            answerGate={answerGate}
            answering={reading}
            onConfirmPayment={(enquiry) => askAboutPayment(enquiry, "confirm")}
            onDeclinePayment={(enquiry) => askAboutPayment(enquiry, "decline")}
          />
        )}

        {!isMobile && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setPage}
            resultCount={visible.length}
          />
        )}
      </section>

      {changing && (
        <ChangeStatusModal
          name={changing.name}
          status={changing.status}
          busy={savingStatus}
          // Not while it is with the server: the move is going out either way,
          // and a dialog closed over it would leave nothing to say so.
          onClose={() => !savingStatus && setChanging(null)}
          onSave={handleSaveStatus}
        />
      )}

      {/* Both are asked before the row changes, because nothing takes either
          answer back: the money is either in or it is not, and this table is
          one of the two places that says which — the other being the enquiry's
          own screen, which asks it with these same two dialogs. */}
      {answering?.kind === "confirm" && (
        <ConfirmPaymentModal
          amount={answering.payment.amount}
          payer={answering.enquiry.name}
          planName={answering.payment.planName}
          duration={answering.payment.duration}
          requestedAt={answering.payment.requestedAt}
          busy={settling}
          onClose={() => !settling && setAnswering(null)}
          onConfirm={handleConfirmPayment}
        />
      )}

      {answering?.kind === "decline" && (
        <DeclinePaymentModal
          amount={answering.payment.amount}
          payer={answering.enquiry.name}
          planName={answering.payment.planName}
          duration={answering.payment.duration}
          busy={settling}
          onClose={() => !settling && setAnswering(null)}
          onConfirm={handleDeclinePayment}
        />
      )}

      {/* Not a screen of its own: the list behind it is perfectly readable, and
          the person may well have come here to work through it rather than to
          add anybody. "Not now" leaves them on it.

          The plan is asked for first where both are missing, so a workspace on
          its first day is walked through them in the order the My brand tabs
          hold them rather than shown two dialogs at once. */}
      {setup.prompting &&
        (noPlans ? (
          <PlansRequiredModal
            onCreatePlan={() => navigate(BRAND_PLANS_PATH)}
            onDismiss={setup.dismiss}
          />
        ) : (
          <ServicesRequiredModal
            onCreateService={() => navigate(BRAND_SERVICES_PATH)}
            onDismiss={setup.dismiss}
          />
        ))}
    </div>
  );
};

export default AllEnquiriesPage;
